// ./src/components/generic/ConfirmModal/ConfirmModal.tsx
//
// The app-wide confirm dialog. Deliberately has NO leading icon: the title already
// names the action and the confirm button carries the colour, so an icon only bought
// an indent that the message had to flow around — and when it failed to render (it was
// passed a Mantine token, "red.6", where a CSS colour was expected) it left a bare gap.
//
// The message often contains a filename or a code, which can be one long unbroken
// token — hence `overflowWrap: anywhere`, without which the dialog grew a horizontal
// scrollbar and clipped the name.

import React from "react";
import { Modal, Text, Group, Stack } from "@mantine/core";
import { useOverlayStack } from "@em/ui";

import { AppButton } from "../AppButton";

export interface ConfirmModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Function called when user confirms */
  onConfirm: () => void;

  /** Modal title */
  title: string;

  /** Main confirmation message */
  message: string;

  /** Optional additional details or warning text */
  details?: string;

  /** Confirm button text */
  confirmLabel?: string;

  /** Cancel button text */
  cancelLabel?: string;

  /** Type of confirmation — decides the confirm button's colour + default label. */
  type?: "danger" | "warning" | "info";

  /** Loading state for confirm action */
  confirmInProgress?: boolean;

  /** Size of the modal */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /**
   * Extra controls rendered between the message and the buttons — a checkbox that changes what the
   * confirm DOES, a reason field, a short summary.
   *
   * ⚠ For a decision the confirm needs, not for more prose (`details` is the prose slot). The case
   * this exists for: withdrawing an approved sponsor, where the host chooses whether money goes back
   * at the same moment they confirm, because the answer differs every time.
   */
  extra?: React.ReactNode;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  opened,
  onClose,
  onConfirm,
  title,
  message,
  details,
  confirmLabel,
  cancelLabel = "Cancel",
  type = "danger",
  confirmInProgress = false,
  size = "md",
  extra,
}) => {
  const isTopOverlay = useOverlayStack(opened);

  // Get default props based on type
  const getTypeDefaults = () => {
    switch (type) {
      case "danger":
        return {
          defaultConfirmLabel: "Delete",
          confirmVariant: "danger" as const,
        };
      case "warning":
        return {
          defaultConfirmLabel: "Continue",
          confirmVariant: "primary" as const,
        };
      case "info":
        return {
          defaultConfirmLabel: "Confirm",
          confirmVariant: "primary" as const,
        };
      default:
        return {
          defaultConfirmLabel: "Confirm",
          confirmVariant: "danger" as const,
        };
    }
  };

  const typeDefaults = getTypeDefaults();
  const finalConfirmLabel = confirmLabel || typeDefaults.defaultConfirmLabel;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    if (!confirmInProgress) {
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={title}
      size={size}
      centered
      closeOnClickOutside={!confirmInProgress}
      // On the overlay stack: Esc closes THIS question, never the drawer it was asked over (and that drawer's
      // shortcuts stand aside while it is open — @em/ui overlayStack).
      closeOnEscape={!confirmInProgress && isTopOverlay}
      withCloseButton={!confirmInProgress}
    >
      <Stack gap="lg">
        {/* Full-width message — no icon gutter to flow around. */}
        <Stack gap="xs">
          <Text size="md" style={{ overflowWrap: "anywhere" }}>
            {message}
          </Text>
          {details && (
            <Text size="md" c="dimmed" style={{ overflowWrap: "anywhere" }}>
              {details}
            </Text>
          )}
          {extra}
        </Stack>

        {/* Action buttons */}
        <Group justify="flex-end" gap="sm">
          <AppButton
            variant="neutral"
            onClick={handleCancel}
            disabled={confirmInProgress}
          >
            {cancelLabel}
          </AppButton>
          <AppButton
            variant={typeDefaults.confirmVariant}
            onClick={handleConfirm}
            inProgress={confirmInProgress}
          >
            {finalConfirmLabel}
          </AppButton>
        </Group>
      </Stack>
    </Modal>
  );
};
